import { useId, useMemo, useState } from 'react';
import { Switch } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { ArrowPathIcon } from '@heroicons/react/16/solid';
import { InlineEditableValue } from '@/components/ui/InlineEditableValue';
import { Checkbox } from '@/components/ui/Checkbox';
import { useCollections } from '@/api/hooks/useCollections';
import { useAuth } from '@/contexts/AuthContext';
import { resolveEffectiveAnnualExpenses, hasValidFiExpense } from './fiExpenses';
import type { Collection, NetWorthProjectionResponse } from '@/api/types';

/** Default FI withdrawal rate percent (the 4% rule); mirrors the hook default. */
const DEFAULT_FI_WITHDRAWAL_RATE_PCT = 4.0;

interface FinancialIndependenceSectionProps {
    /** Projection data from API */
    projectionData: NetWorthProjectionResponse;
    /** Whether the FI view is enabled */
    fiEnabled: boolean;
    /** Annual expenses override for FI target (null = use applied value) */
    annualExpenses: number | null;
    /** Withdrawal rate percent driving the FI multiplier (e.g. 4.0 = 25x) */
    fiWithdrawalRatePct: number;
    /** Currently included collection IDs; null = all included (default) */
    includedCollectionIds: number[] | null;
    /** FI-specific override for the investable bucket return rate; null = auto-derived */
    fiInvestableReturnRate?: number | null;
    /** True while fresh projection data is being fetched (suppresses the adjusted note) */
    isRefreshing?: boolean;
    /** Callback when the FI view toggle changes */
    onFiEnabledChange: (enabled: boolean) => void;
    /** Callback when annual expenses changes */
    onAnnualExpensesChange: (amount: number) => void;
    /** Callback when the FI withdrawal rate percent changes */
    onFiWithdrawalRatePctChange: (pct: number) => void;
    /** Callback when the included FI collections change (null = all) */
    onIncludedCollectionIdsChange: (ids: number[] | null) => void;
    /** Callback when the FI investable return rate changes */
    onFiInvestableReturnRateChange?: (rate: number) => void;
    /** Callback to reset the FI investable return rate to the derived value */
    onFiInvestableReturnRateReset?: () => void;
    /** Whether the post-FI withdrawal simulation is on (opt-in, default off) */
    fiSimulateWithdrawal?: boolean;
    /** Inflation-rate override for the real-terms simulation (decimal); null = server default */
    fiInflationRate?: number | null;
    /** Callback when the withdrawal-simulation toggle changes */
    onFiSimulateWithdrawalChange?: (enabled: boolean) => void;
    /** Callback when the inflation-rate override changes */
    onFiInflationRateChange?: (rate: number) => void;
    /** Callback to reset the inflation rate to the config default */
    onFiInflationRateReset?: () => void;
    /**
     * Callback to reset the whole FI section back to its defaults (expenses,
     * withdrawal rate, collections, investable rate, and the withdrawal
     * simulation). Leaves the section open (does not touch `fiEnabled`).
     */
    onResetFiSettings?: () => void;
    /** Callback to reset only the post-FI withdrawal simulation to its defaults */
    onResetWithdrawalSimulation?: () => void;
}

/**
 * Self-contained financial-independence section. The toggle is the header;
 * when enabled it expands to reveal the FI explanation, the adjustable
 * expenses and withdrawal-rate values, and the investable-collections picker.
 */
export function FinancialIndependenceSection({
    projectionData,
    fiEnabled,
    annualExpenses,
    fiWithdrawalRatePct,
    includedCollectionIds,
    fiInvestableReturnRate = null,
    fiSimulateWithdrawal = false,
    fiInflationRate = null,
    isRefreshing = false,
    onFiEnabledChange,
    onAnnualExpensesChange,
    onFiWithdrawalRatePctChange,
    onIncludedCollectionIdsChange,
    onFiInvestableReturnRateChange,
    onFiInvestableReturnRateReset,
    onFiSimulateWithdrawalChange,
    onFiInflationRateChange,
    onFiInflationRateReset,
    onResetFiSettings,
    onResetWithdrawalSimulation,
}: FinancialIndependenceSectionProps) {
    const { currency } = projectionData;
    const { config } = useAuth();
    const bodyId = useId();
    const pickerPanelId = useId();

    // The collections picker is its own collapsible disclosure, collapsed by default.
    const [pickerOpen, setPickerOpen] = useState(false);

    // Fetch by type so the backend returns every asset collection (the type
    // filter bypasses pagination), keeping the picker and the "all" set complete.
    const { data, isLoading } = useCollections({ type: 'asset' });

    const assetCollections = useMemo<Collection[]>(() => {
        const items = data?.items ?? [];
        return items.filter(collection => collection.type === 'asset');
    }, [data?.items]);

    // null means "all included" — reflect that as every box checked.
    const isChecked = (id: number): boolean =>
        includedCollectionIds === null || includedCollectionIds.includes(id);

    // Number of asset collections currently counted toward FI.
    const checkedCount = assetCollections.filter(c => isChecked(c.id)).length;

    const handleToggleCollection = (collection: Collection, checked: boolean): void => {
        // Refuse to uncheck the last remaining collection: an empty investable
        // set has no meaning, and silently collapsing it to "all included" is
        // the opposite of what unchecking implies.
        if (!checked && checkedCount <= 1) {
            return;
        }
        // Materialize the implicit "all" selection before removing one item.
        const current = includedCollectionIds ?? assetCollections.map(c => c.id);
        const next = checked
            ? Array.from(new Set([...current, collection.id]))
            : current.filter(id => id !== collection.id);
        // Empty selection — or every asset collection checked — collapses back to
        // the "all included" default (null), so the backend runs the degenerate
        // single-engine path instead of treating it as an explicit full-ID subset.
        const everySelected = assetCollections.length > 0 && next.length >= assetCollections.length;
        onIncludedCollectionIdsChange(next.length === 0 || everySelected ? null : next);
    };

    // The expenses input falls back to the applied value when no override is set.
    const effectiveAnnualExpenses = resolveEffectiveAnnualExpenses(annualExpenses, projectionData.appliedAnnualExpenses, 0);
    // A valid expense is required to project the FI target and year.
    const hasValidExpense = hasValidFiExpense(annualExpenses, projectionData.appliedAnnualExpenses);

    // Compositional mode: a real subset is selected (not all, not none). Only
    // then does the investable bucket grow at its own rate, so the editable
    // investable-rate knob is shown here; otherwise the header knob is the sole
    // control (decisions 3/5).
    const isRealSubset = includedCollectionIds !== null
        && checkedCount > 0
        && checkedCount < assetCollections.length;

    const appliedInvestableReturnRate = projectionData.appliedInvestableReturnRate ?? null;
    const hasInvestableOverride = fiInvestableReturnRate !== null;
    const investableRateValue = fiInvestableReturnRate ?? appliedInvestableReturnRate ?? 0;
    const showInvestableRateKnob = isRealSubset && !!onFiInvestableReturnRateChange;
    // Surface backend clamping like the header's adjusted-note pattern.
    const investableRateAdjusted = !isRefreshing
        && hasInvestableOverride
        && appliedInvestableReturnRate !== null
        && Math.abs(appliedInvestableReturnRate - (fiInvestableReturnRate ?? 0)) > 0.0005;

    // Post-FI withdrawal simulation. The default inflation assumption comes from
    // server config (mirrors projectionYears); the knob shows the override when
    // set, otherwise that default. The toggle is only meaningful once a valid
    // expense target exists, so it is hidden without one.
    const defaultInflationRate = config?.analytics.defaultInflationRate ?? 0.02;
    const inflationValue = fiInflationRate ?? defaultInflationRate;
    const hasInflationOverride = fiInflationRate !== null;
    const appliedInflationRate = projectionData.appliedInflationRate ?? null;
    const withdrawalStartYear = projectionData.withdrawalStartYear ?? null;
    const fundsDepletedYear = projectionData.fundsDepletedYear ?? null;
    const showWithdrawalToggle = hasValidExpense && !!onFiSimulateWithdrawalChange;
    // Surface backend clamping with the same amber "adjusted" note pattern.
    const inflationAdjusted = !isRefreshing
        && fiSimulateWithdrawal
        && hasInflationOverride
        && appliedInflationRate !== null
        && Math.abs(appliedInflationRate - (fiInflationRate ?? 0)) > 0.0005;

    // Whether any FI setting differs from its default — drives the section-level
    // reset affordance. Excludes `fiEnabled` (that toggle is the section header,
    // not a setting) and the header/total-line fields (their own reset covers them).
    const fiSettingsModified =
        annualExpenses !== null
        || fiWithdrawalRatePct !== DEFAULT_FI_WITHDRAWAL_RATE_PCT
        || includedCollectionIds !== null
        || hasInvestableOverride
        || fiSimulateWithdrawal
        || hasInflationOverride;

    // Whether the withdrawal simulation differs from its default — drives the
    // sub-card reset affordance.
    const withdrawalSimulationModified = fiSimulateWithdrawal || hasInflationOverride;

    // Short summary shown on the collapsed picker header. Count only IDs that
    // still exist among the fetched asset collections so the number matches the
    // visible checkboxes — stored IDs of deleted collections are ignored.
    const pickerSummary =
        includedCollectionIds === null
            ? 'All included'
            : `${checkedCount} selected`;

    return (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            {/* Header row: the toggle. Acts as the section header / disclosure trigger. */}
            <div className="flex items-center justify-between gap-3 px-4 py-2.5">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                    Financial independence
                </span>
                <div className="flex items-center gap-2">
                    {fiSettingsModified && onResetFiSettings && (
                        <button
                            type="button"
                            onClick={onResetFiSettings}
                            title="Reset financial independence settings"
                            className="inline-flex items-center align-middle text-gray-400 hover:text-accent dark:text-gray-500 dark:hover:text-accent transition-colors"
                            aria-label="Reset financial independence settings"
                        >
                            <ArrowPathIcon className="w-3.5 h-3.5" />
                        </button>
                    )}
                    <Switch
                        checked={fiEnabled}
                        onChange={onFiEnabledChange}
                        aria-controls={bodyId}
                        aria-expanded={fiEnabled}
                        className={`group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 ${
                            fiEnabled ? 'bg-accent' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                        aria-label="Toggle financial independence view"
                    >
                        <span
                            aria-hidden="true"
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                fiEnabled ? 'translate-x-5' : 'translate-x-0'
                            }`}
                        />
                    </Switch>
                </div>
            </div>

            {/* Expanding body: only present when FI is enabled. */}
            {fiEnabled && (
                <div
                    id={bodyId}
                    className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 space-y-3"
                >
                    {/* FI explanation with the adjustable expenses and rate values. */}
                    <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                        The solid line is your total net worth. The second line tracks your investable
                        assets (the selected collections, with debts inside them netted) — that line is
                        what the FI target and year are measured against. With annual expenses of{' '}
                        <InlineEditableValue
                            value={effectiveAnnualExpenses}
                            onChange={onAnnualExpensesChange}
                            format="currency"
                            currency={currency}
                            min={0}
                            max={1000000000}
                            step={1000}
                        />{' '}
                        and a withdrawal rate of{' '}
                        <InlineEditableValue
                            value={fiWithdrawalRatePct / 100}
                            onChange={rate => onFiWithdrawalRatePctChange(rate * 100)}
                            format="percent"
                            min={0.001}
                            max={0.99}
                            step={0.1}
                        />
                        <span className="ml-1 text-gray-500 dark:text-gray-400">
                            → {(100 / fiWithdrawalRatePct).toFixed(1)}× annual expenses
                        </span>
                        , your financial independence target is expenses × that multiplier (the 4% rule by default).
                    </p>

                    {/* Compositional-model explainer: shown only with a real subset, where
                        the investable bucket grows at its own (editable) rate. */}
                    {showInvestableRateKnob && (
                        <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                            Your investable line grows at{' '}
                            <InlineEditableValue
                                value={investableRateValue}
                                onChange={onFiInvestableReturnRateChange!}
                                format="percent"
                                min={-0.5}
                                max={0.5}
                                step={0.1}
                            />{' '}
                            annually{' '}
                            {hasInvestableOverride ? (
                                <>(your override)</>
                            ) : (
                                <>(derived from your selected collections' history)</>
                            )}
                            {hasInvestableOverride && onFiInvestableReturnRateReset && (
                                <button
                                    type="button"
                                    onClick={onFiInvestableReturnRateReset}
                                    title="Reset to the derived investable rate"
                                    className="ml-1 inline-flex items-center align-middle text-gray-400 hover:text-accent dark:text-gray-500 dark:hover:text-accent transition-colors"
                                    aria-label="Reset investable return rate"
                                >
                                    <ArrowPathIcon className="w-3.5 h-3.5" />
                                </button>
                            )}
                            {' '}and receives all of your contributions. Collections you leave unchecked
                            are frozen at today's value in the projection (no growth, no loss), and
                            outside debts follow your debt trend — so your net worth line is simply the
                            sum of those parts.
                        </p>
                    )}

                    {investableRateAdjusted && (
                        <p role="status" className="text-xs text-amber-600 dark:text-amber-400">
                            Investable rate was adjusted to {((appliedInvestableReturnRate ?? 0) * 100).toFixed(1)}% (allowed range −100% to 100%).
                        </p>
                    )}

                    {/* Hint shown until a valid annual expense is entered. Without it the
                        FI target, year, and investable line cannot be projected. */}
                    {!hasValidExpense && (
                        <p
                            role="status"
                            className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300"
                        >
                            Enter your annual expenses to project your financial-independence target and year.
                        </p>
                    )}

                    {/* Post-FI withdrawal simulation: toggle + inflation knob + surfaced years.
                        Hidden until a valid annual expense (the FI target) exists. */}
                    {showWithdrawalToggle && (
                        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/40 px-4 py-3 space-y-2">
                            <div className="flex items-center justify-between gap-3">
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    Simulate withdrawals after FI
                                </span>
                                <div className="flex items-center gap-2">
                                    {withdrawalSimulationModified && onResetWithdrawalSimulation && (
                                        <button
                                            type="button"
                                            onClick={onResetWithdrawalSimulation}
                                            title="Reset withdrawal simulation settings"
                                            className="inline-flex items-center align-middle text-gray-400 hover:text-accent dark:text-gray-500 dark:hover:text-accent transition-colors"
                                            aria-label="Reset withdrawal simulation settings"
                                        >
                                            <ArrowPathIcon className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                    <Switch
                                        checked={fiSimulateWithdrawal}
                                        onChange={onFiSimulateWithdrawalChange}
                                        className={`group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 ${
                                            fiSimulateWithdrawal ? 'bg-accent' : 'bg-gray-300 dark:bg-gray-600'
                                        }`}
                                        aria-label="Toggle simulate withdrawals after FI"
                                    >
                                        <span
                                            aria-hidden="true"
                                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                                fiSimulateWithdrawal ? 'translate-x-5' : 'translate-x-0'
                                            }`}
                                        />
                                    </Switch>
                                </div>
                            </div>

                            {fiSimulateWithdrawal && (
                                <>
                                    <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                                        The whole projection runs in today's money, with growth reduced by an
                                        assumed inflation of{' '}
                                        <InlineEditableValue
                                            value={inflationValue}
                                            onChange={onFiInflationRateChange!}
                                            format="percent"
                                            min={0}
                                            max={0.2}
                                            step={0.1}
                                        />
                                        {hasInflationOverride && onFiInflationRateReset && (
                                            <button
                                                type="button"
                                                onClick={onFiInflationRateReset}
                                                title="Reset to the default inflation rate"
                                                className="ml-1 inline-flex items-center align-middle text-gray-400 hover:text-accent dark:text-gray-500 dark:hover:text-accent transition-colors"
                                                aria-label="Reset inflation rate"
                                            >
                                                <ArrowPathIcon className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                        . Every value stays in today's money (real terms), so your FI target
                                        needs no adjusting — expect FI to land a little later than the nominal
                                        view (that is the honest math). Collections you leave unchecked stay
                                        frozen, which in real terms means they keep pace with inflation. From the
                                        year after you reach FI your contributions stop and your annual expenses
                                        are withdrawn each year.
                                    </p>

                                    {inflationAdjusted && (
                                        <p role="status" className="text-xs text-amber-600 dark:text-amber-400">
                                            Inflation rate was adjusted to {((appliedInflationRate ?? 0) * 100).toFixed(1)}% (allowed range 0% to 20%).
                                        </p>
                                    )}

                                    {withdrawalStartYear != null && (
                                        <p className="text-sm text-gray-600 dark:text-gray-300">
                                            Withdrawals start in {withdrawalStartYear}.
                                        </p>
                                    )}

                                    {fundsDepletedYear != null && (
                                        <p
                                            role="status"
                                            className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300"
                                        >
                                            Pot runs out in {fundsDepletedYear}.
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {/* Investable-collections picker as a collapsible disclosure, collapsed by default. */}
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/40">
                        <button
                            type="button"
                            onClick={() => setPickerOpen(open => !open)}
                            aria-expanded={pickerOpen}
                            aria-controls={pickerPanelId}
                            className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent rounded-lg"
                        >
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                                Investable collections
                            </span>
                            <span className="flex items-center gap-2">
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {pickerSummary}
                                </span>
                                <ChevronDownIcon
                                    aria-hidden="true"
                                    className={`h-4 w-4 text-gray-400 transition-transform ${pickerOpen ? 'rotate-180' : ''}`}
                                />
                            </span>
                        </button>
                        {pickerOpen && (
                            <div
                                id={pickerPanelId}
                                className="border-t border-gray-200 dark:border-gray-700 px-4 py-3"
                            >
                                <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                                    Choose which collections count toward your FI target. Only asset
                                    collections are shown. Leaving all checked counts every collection.
                                </p>
                                {isLoading ? (
                                    <p className="text-sm text-gray-500 dark:text-gray-400">Loading collections…</p>
                                ) : assetCollections.length === 0 ? (
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        No asset collections yet.
                                    </p>
                                ) : (
                                    <div className="space-y-2">
                                        {assetCollections.map(collection => (
                                            <Checkbox
                                                key={collection.id}
                                                label={collection.label}
                                                checked={isChecked(collection.id)}
                                                // Keep at least one collection counted toward FI.
                                                disabled={isChecked(collection.id) && checkedCount <= 1}
                                                onChange={e => handleToggleCollection(collection, e.target.checked)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
